import { useCallback, useRef, useState } from 'react';

import ConfirmDialog from './ConfirmDialog';

const DEFAULT_OPTIONS = {
  title: '確認',
  message: '',
  confirmText: '確認',
  cancelText: '取消',
  showCancel: true,
  destructive: false,
  titleSrOnly: false,
  closeOnOutsideClick: true,
  closeOnEscape: true,
  /** 預設關閉；呼叫端 opt-in，禁止當成全域預設行為。 */
  confirmOnEnterSpace: false,
};

function useConfirmDialog() {
  const resolveRef = useRef(null);
  const [dialogState, setDialogState] = useState({
    open: false,
    ...DEFAULT_OPTIONS,
  });

  const finish = useCallback((result) => {
    if (!resolveRef.current) {
      return;
    }

    const resolve = resolveRef.current;
    resolveRef.current = null;
    setDialogState((prev) => ({ ...prev, open: false }));
    resolve(result);
  }, []);

  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setDialogState({
        open: true,
        ...DEFAULT_OPTIONS,
        ...options,
      });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    finish(true);
  }, [finish]);

  const handleCancel = useCallback(() => {
    finish(false);
  }, [finish]);

  const handleOpenChange = useCallback((nextOpen) => {
    if (!nextOpen) {
      finish(false);
      return;
    }

    setDialogState((prev) => ({ ...prev, open: true }));
  }, [finish]);

  // 回傳 JSX 節點（非 function component），避免每次 render 換型別導致 remount，
  // 進而重置 confirmOnEnterSpace 的 arm timer。
  const confirmDialog = (
    <ConfirmDialog
      open={dialogState.open}
      onOpenChange={handleOpenChange}
      title={dialogState.title}
      titleSrOnly={dialogState.titleSrOnly}
      message={dialogState.message}
      confirmText={dialogState.confirmText}
      cancelText={dialogState.cancelText}
      showCancel={dialogState.showCancel}
      destructive={dialogState.destructive}
      closeOnOutsideClick={dialogState.closeOnOutsideClick}
      closeOnEscape={dialogState.closeOnEscape}
      confirmOnEnterSpace={dialogState.confirmOnEnterSpace}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    />
  );

  return {
    confirm,
    confirmDialog,
    /** @deprecated 請改用 confirmDialog（JSX 節點）。保留別名以免漏改。 */
    ConfirmDialogHost: confirmDialog,
  };
}

export default useConfirmDialog;
